import { DataNumberFormat, SortDirection } from '../../../../services/api.services';
import { DataType } from '../../../dataModel/DataType';
import { VisualField } from '../../VisualField';
import { DataPoint, DataPointCollection, DataPointField } from '../../VisualInteractions';
import { VisualComponent } from '../../base/Visual.component';
import { Rows } from '../../data';
import { DataTooltip, DataTooltipRow, DataTooltipRowStyle } from '../../tooltip/DataTooltipComponent';

function getSortDirectionMultiplier(sortDirection: SortDirection) {
  return (sortDirection == SortDirection.Ascending) ? +1 : -1;
}

const MAX_TOP_VALUES = 5;

export class WaterfallValue {
  constructor(
    readonly period: WaterfallPeriod,
    readonly category: WaterfallCategory,
    readonly value: number,
    readonly dataPoint: DataPoint) { }
}

export interface WaterfallChange {
  readonly from: WaterfallValue;
  readonly to: WaterfallValue;
  readonly delta: number;
}

export class WaterfallPeriod {
  constructor(
    readonly builder: WaterfallBuilder,
    readonly key: any,
    readonly displayText: string,
    readonly dataPoint: DataPoint) { }

  readonly values = new Map<WaterfallCategory, WaterfallValue>();

  topChanges: WaterfallChange[];
  otherChanges: WaterfallChange[];

  total = 0;
  totalDisplayText = '';

  _computeIncreasesAndDecreases(nextPeriod: WaterfallPeriod) {
    let changes: WaterfallChange[] = [];

    let visited = new Set();

    for (let [, from] of this.values) {
      visited.add(from.category);

      let to = nextPeriod.values.get(from.category);
      let delta = (to?.value ?? 0) - (from?.value ?? 0);
      changes.push({ from, to, delta });
    }

    for (let [, to] of nextPeriod.values) {
      if (visited.has(to.category))
        continue;

      let delta = to.value;
      changes.push({ from: null, to, delta });
    }

    let increases = changes.filter(x => x.delta >= 0);
    let decreases = changes.filter(x => x.delta < 0);

    increases.sort((a, b) => b.delta - a.delta);
    decreases.sort((a, b) => a.delta - b.delta);

    let sortedChanges = [...increases, ...decreases].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
    this.topChanges = sortedChanges.slice(0, MAX_TOP_VALUES);
    this.otherChanges = sortedChanges.slice(MAX_TOP_VALUES);
  }

  _updateTotal() {
    for (let [, v] of this.values) {
      this.total += v.value;
    }

    this.totalDisplayText = this.builder.formatter.getValueDisplay(this.builder.measureField, this.total);
  }
}

export class WaterfallCategory {
  constructor(
    readonly key: any,
    readonly displayText: string) { }
}

export class WaterfallBuilder {
  readonly periods = new Map<any, WaterfallPeriod>()
    .withTreatUndefinedAsNull()
    .withDefaultFactory(key => new WaterfallPeriod(
      this,
      key,
      this.formatter.getValueDisplay(this.periodField, key),
      this.dataPoints.get([{
        field: this.periodField,
        value: key
      }])));

  readonly categories = new Map<any, WaterfallCategory>()
    .withTreatUndefinedAsNull()
    .withDefaultFactory(key => new WaterfallCategory(
      key,
      this.formatter.getValueDisplay(this.categoryField, key)));

  constructor(
    readonly periodField: VisualField,
    readonly categoryField: VisualField,
    readonly measureField: VisualField,
    readonly formatter: VisualComponent.RowFormatter,
    readonly dataPoints: DataPointCollection) { }

  private populateFromRows(rows: Rows) {
    let nullCategory = new WaterfallCategory(null, '');
    this.categories.set(null, nullCategory);

    for (let row of rows) {
      let categoryKey =
        this.categoryField
          ? (this.formatter.getRowValueData(row, this.categoryField) ?? null)
          : null;

      let category = this.categories.getOrCreate(categoryKey);

      let periodKey = this.formatter.getRowValueData(row, this.periodField)   ?? null;
      let period    = this.periods.getOrCreate(periodKey);

      let dataPointFields: DataPointField[] = [{
        field: this.periodField,
        value: periodKey
      }];

      if (this.categoryField) {
        dataPointFields.push({
          field: this.categoryField,
          value: categoryKey
        });
      }

      let measureValue = this.formatter.getRowValueData(row, this.measureField)  ?? null;

      let value = new WaterfallValue(
        period,
        category,
        measureValue,
        this.dataPoints.get(dataPointFields));

      period.values.set(category, value);
    }
  }

  private getSortedPeriods() {
    let periods = [...this.periods.values()];
    let direction = this.periodField.sorted() ? this.periodField.sortDirection() : SortDirection.Ascending;

    let sortOrderMultiplier = getSortDirectionMultiplier(direction);
    let sortFunc = DataType.getSortFunction(this.periodField.dataType, { convertText: true });
    periods.sort((a, b) => sortFunc(a.key, b.key) * sortOrderMultiplier);

    return periods;
  }

  createWaterfall(rows: Rows) {
    this.populateFromRows(rows);
    let sortedPeriods = this.getSortedPeriods();
    
    sortedPeriods.forEach(x => x._updateTotal());

    for (let i = 0; i < sortedPeriods.length - 1; ++i) {
      let current = sortedPeriods[i];
      let next = sortedPeriods[i + 1];
      current._computeIncreasesAndDecreases(next);
    }

    return new Waterfall(this, sortedPeriods);
  }
}


export enum WaterfallChartItemType {
  Total,
  Increase,
  Decrease,
}

export interface WaterfallChartItem {
  readonly type: WaterfallChartItemType;
  readonly displayText: string;
  readonly value: number;
  readonly dataPoints: ReadonlyArray<DataPoint>;
  readonly tooltip: DataTooltip;
  readonly isOther?: boolean;
}

export class Waterfall {
  readonly items: ReadonlyArray<WaterfallChartItem>;

  constructor(
    builder: WaterfallBuilder,
    periods: ReadonlyArray<WaterfallPeriod>) {
    let items: WaterfallChartItem[] = [];

    for (let i = 0; i < periods.length - 1; ++i) {
      let p = periods[i];
      let next = periods[i + 1];

      items.push({
        type: WaterfallChartItemType.Total,
        displayText: p.displayText,
        value: p.total,
        dataPoints: [p.dataPoint],
        tooltip: {
          headers: builder.categoryField ? [{
            name: builder.categoryField.shortTitle,
            value: 'All'
          }] : null,
          columns: [{
            name: builder.periodField.shortTitle,
            values: [builder.measureField.shortTitle]
          }],
          rows: [{
            color: '#004c78',
            name:  p.displayText,
            values: [p.totalDisplayText],
            style: DataTooltipRowStyle.Default
          }]
        }
      });
      
      let periodItems: WaterfallChartItem[] = [];

      function getPercentageChangeText(change: number, total: number) {
        let value = total ? (change / total) : null
         return value
            ? builder.formatter.getValueDisplay(builder.measureField, value, { includePlusSign: true, display: {
              numberFormat: DataNumberFormat.Percentage
            } })
            : null;
      }

      function pushChange(x: WaterfallChange, total: number) {
        let deltaText = builder.formatter.getValueDisplay(builder.measureField, x.delta, { includePlusSign: true });

        let tooltipRows: DataTooltipRow[] = [{
          name: p.displayText,
          values: [builder.formatter.getValueDisplay(builder.measureField, x.from?.value ?? 0)],
          style: DataTooltipRowStyle.Default
        }];

        tooltipRows.push({
          name: next.displayText,
          values: [builder.formatter.getValueDisplay(builder.measureField, x.to?.value ?? 0)],
          style: DataTooltipRowStyle.Default
        });

        tooltipRows.push({
          color: x.delta < 0 ? '#B33A46' : '#158A34',
          name: 'Change',
          nameSuffix: getPercentageChangeText(x.delta, total),
          values: [deltaText],
          style: DataTooltipRowStyle.Change
        });

        let categoryName = (x.from ?? x.to).category.displayText;

        let type = x.delta < 0 ? WaterfallChartItemType.Decrease : WaterfallChartItemType.Increase;

        periodItems.push({
          type,
          displayText: categoryName,
          value: x.delta,
          dataPoints: [x.from?.dataPoint, x.to?.dataPoint].filter(x => !!x),
          tooltip: {
            headers: builder.categoryField ? [{
              name: builder.categoryField.shortTitle,
              value: categoryName
            }] : [],
            columns: [{
              name: builder.periodField.shortTitle,
              values: [builder.measureField.shortTitle]
            }],

            rows: tooltipRows
          },
        });
      }

      for (let x of p.topChanges) {
        pushChange(x, p.total);
      }

      if (p.otherChanges.length) {
        let delta = p.otherChanges.reduce((n, x) => n + x.delta, 0);
        let deltaText = builder.formatter.getValueDisplay(builder.measureField, delta, { includePlusSign: true });
        
        let currTotal = p.otherChanges.filter(x => !!x.from).reduce((n, x) => n + x.from.value, 0);
        let tooltipRows: DataTooltipRow[] = [{
          name: p.displayText,
          values: [builder.formatter.getValueDisplay(builder.measureField, currTotal)],
          style: DataTooltipRowStyle.Default
        }];

        let nextTotal = p.otherChanges.filter(x => !!x.to).reduce((n, x) => n + x.to.value, 0);
        tooltipRows.push({
          name: next.displayText,
          values: [builder.formatter.getValueDisplay(builder.measureField, nextTotal)],
          style: DataTooltipRowStyle.Default
        });

        tooltipRows.push({
          color: delta < 0 ? '#B33A46' : '#158A34',
          name: 'Change',
          nameSuffix: getPercentageChangeText(delta, p.total),
          values: [deltaText],
          style: DataTooltipRowStyle.Change
        });

        let type = (delta < 0) ? WaterfallChartItemType.Decrease : WaterfallChartItemType.Increase;
        periodItems.push({
          type,
          displayText: 'Other',
          value: delta,
          dataPoints: p.otherChanges.flatMap(x => [x.from?.dataPoint, x.to?.dataPoint]).filter(x => !!x),
          tooltip: {
            headers: builder.categoryField ? [{
              name: builder.categoryField.shortTitle,
              value: `${p.otherChanges.length} Other`
            }] : [],
            columns: [{
              name: builder.periodField.shortTitle,
              values: [builder.measureField.shortTitle]
            }],

            rows: tooltipRows
          },
          isOther: true,
        });
      }

      periodItems.sort((a, b) => {
        let d = (a.type == WaterfallChartItemType.Increase ? -1 : +1)
              - (b.type == WaterfallChartItemType.Increase ? -1 : +1);

        if (d == 0) 
          d = Math.abs(b.value) - Math.abs(a.value);
        
        return d;
      });

      items.push(...periodItems);
    }

    let lastPeriod = periods[periods.length - 1];
    items.push({
      type: WaterfallChartItemType.Total,
      displayText: lastPeriod.displayText,
      value: lastPeriod.total,
      dataPoints: [lastPeriod.dataPoint],
      tooltip: {
        headers: builder.categoryField ? [{
          name: builder.categoryField.shortTitle,
          value: 'All'
        }] : null,
        columns: [{
          name: builder.periodField.shortTitle,
          values: [builder.measureField.shortTitle]
        }],
        rows: [{
          color: '#004c78',
          name:  lastPeriod.displayText,
          values: [lastPeriod.totalDisplayText],
          style: DataTooltipRowStyle.Default
        }]
      }
    });

    this.items = items;
  }
}
